import React, { useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../../hooks/use-toast';

function CommentItem({ comment, replies, onReply }) {
  const [showReply, setShowReply] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submitReply = async () => {
    if (!replyBody.trim()) return;
    setSubmitting(true);
    try {
      await onReply(comment.id, replyBody.trim());
      setReplyBody('');
      setShowReply(false);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message || 'Failed to reply', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex justify-between gap-2">
        <p className="text-sm font-medium">{comment.author_name}</p>
        <p className="text-xs text-muted-foreground">{comment.created_at}</p>
      </div>
      <p className="text-sm mt-2 whitespace-pre-wrap">{comment.body}</p>
      <Button type="button" variant="ghost" size="sm" className="mt-2 h-7 px-2" onClick={() => setShowReply(!showReply)}>
        Reply
      </Button>
      {showReply && (
        <div className="mt-2 space-y-2">
          <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Write a reply… Use @employee_code to mention" rows={3} />
          <Button size="sm" onClick={submitReply} disabled={submitting}>Post Reply</Button>
        </div>
      )}
      {replies.length > 0 && (
        <div className="mt-3 ml-4 space-y-2 border-l-2 pl-3">
          {replies.map((r) => (
            <div key={r.id} className="text-sm">
              <p className="font-medium">{r.author_name} <span className="text-xs text-muted-foreground font-normal">{r.created_at}</span></p>
              <p className="whitespace-pre-wrap mt-1">{r.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CrmTicketComments({ ticketId, comments = [], onAdded }) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const roots = comments.filter((c) => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) {
      acc[c.parent_id] = acc[c.parent_id] || [];
      acc[c.parent_id].push(c);
    }
    return acc;
  }, {});

  const postComment = async (parentId, text) => {
    await api.post(`/crm/tickets/${ticketId}/comments`, { body: text, parent_id: parentId || undefined });
    if (!parentId) {
      setBody('');
    }
    onAdded?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await postComment(null, body.trim());
      toast({ title: 'Comment added' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-2">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment… Use @employee_code to mention someone" rows={4} />
        <Button type="submit" disabled={submitting}>Post Comment</Button>
      </form>
      <div className="space-y-3">
        {roots.map((c) => (
          <CommentItem key={c.id} comment={c} replies={repliesByParent[c.id] || []} onReply={postComment} />
        ))}
        {roots.length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
      </div>
    </div>
  );
}
